import React, { lazy } from "react";
import { useQuery } from "react-query";
import ProductService from "../../services/productService";
import { BannerResponse } from "../../types/BannerResponse";
import HomeTopBanner from "./components/Banner";
import FindYourNextPlan from "./components/FindYourNextPlan";
import MajorTourDest from "./components/MajorTourDest";
import RecommendAttractionPass from "./components/RecommendAttractionPass";

import { BotNav } from "../../components/layout/BotNav";
import { LazySection } from "../../components/LazySection";
import { Helmet } from "react-helmet-async";

const RecommendTicket = lazy(() =>
  import("./components/RecommendTicket").then((m) => ({ default: m.default })),
);
const RecommendGuidedTour = lazy(() =>
  import("./components/RecommendGuidedTour").then((m) => ({
    default: m.default,
  })),
);
const HomeBotBanner = lazy(() =>
  import("./HomeBotBanner").then((m) => ({ default: m.HomeBotBanner })),
);
const MusicalSwiper = lazy(() =>
  import("./components/MusicalSwiper").then((m) => ({ default: m.default })),
);
const EventSwiper = lazy(() =>
  import("./components/EventSwiper").then((m) => ({ default: m.default })),
);
const EsimSwiper = lazy(() =>
  import("./components/EsimSwiper").then((m) => ({ default: m.default })),
);
const HomeMyInquiry = lazy(() =>
  import("./HomeMyInquiry").then((m) => ({ default: m.HomeMyInquiry })),
);

const HomePage: React.FC = () => {
  const { data: bannerData } = useQuery<BannerResponse>(
    ["banner"],
    async () => {
      const result = await ProductService.getBanner();
      if (!result) {
        throw new Error("Failed to fetch home page data");
      }
      return result;
    },
  );

  const schema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "Tamice",
    url: window.location.href,
    contactPoint: {
      "@type": "ContactPoint",
      telephone: "1-646-684-4848",
      contactType: "customer service",
    },
  };

  return (
    <>
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(schema)}</script>
      </Helmet>
      <div>
        <FindYourNextPlan banners={bannerData?.mainBanner || []} />
        {/* This main content with padding */}
        <div className="ui-container">
          <HomeTopBanner
            banners={bannerData?.topBanner || []}
            bannerPopup={bannerData?.popupBanner || []}
          />
          <MajorTourDest className="pt-10 sm:min-h-[394px] sm:pt-[4.5rem]" />
        </div>
        <div className="bg-[#F7F8FA] sm:min-h-[530px]">
          <div className="ui-container">
            <RecommendAttractionPass className="mt-10 py-10 sm:mt-20 sm:py-20" />
          </div>
        </div>
        {/* Lazy load from here */}
        <div className="ui-container">
          <LazySection className="pt-10 sm:min-h-[442px] sm:pt-[4.5rem]">
            <RecommendTicket className="pt-10 sm:min-h-[442px] sm:pt-[4.5rem]" />
          </LazySection>
          <LazySection className="pt-10 sm:min-h-[494px] sm:pt-[4.5rem]">
            <RecommendGuidedTour className="pt-10 sm:min-h-[494px] sm:pt-[4.5rem]" />
          </LazySection>
          <div className="pt-10 sm:pt-[4.5rem]" />
          <LazySection className="sm:min-h-[240px]">
            <HomeBotBanner className="sm:min-h-[240px]" />
          </LazySection>
          <LazySection className="mx-auto pt-10 sm:min-h-[414px] sm:pt-[4.5rem]">
            <MusicalSwiper />
          </LazySection>
          <LazySection className="mx-auto pt-10 sm:min-h-[230px] sm:max-w-[84.125rem] sm:pt-[4.5rem]">
            <EventSwiper />
          </LazySection>
          <LazySection className="mx-auto pt-10 sm:min-h-[441px] sm:pt-[4.5rem]">
            <EsimSwiper />
          </LazySection>
          <LazySection className="">
            <HomeMyInquiry />
          </LazySection>
        </div>
      </div>
      <BotNav active="HOME" />
    </>
  );
};

export default HomePage;
